import { useEffect } from 'react'
import { useNavigate } from 'react-router'

// Confirms before an edited page is left. The app mounts a declarative <BrowserRouter>, so
// react-router's useBlocker — data routers only — is not available: the click that would navigate
// is intercepted instead, in the capture phase, before React's own handlers see it.
export function useUnsavedChangesGuard(
  active: boolean,
  confirmLeave: () => Promise<boolean>
) {
  const navigate = useNavigate()

  useEffect(() => {
    if (!active) return

    const warnOnUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      // Still required by Chrome and Safari to raise the native dialog.
      event.returnValue = ''
    }

    const interceptClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        return

      const clicked = (event.target as Element | null)?.closest(
        'a[href], [data-nav-to]'
      )
      const to = navigationTarget(clicked ?? null)
      if (!to || to === window.location.pathname + window.location.search)
        return

      event.preventDefault()
      event.stopPropagation()
      confirmLeave().then((confirmed) => confirmed && navigate(to))
    }

    window.addEventListener('beforeunload', warnOnUnload)
    document.addEventListener('click', interceptClick, true)

    return () => {
      window.removeEventListener('beforeunload', warnOnUnload)
      document.removeEventListener('click', interceptClick, true)
    }
  }, [active, confirmLeave, navigate])
}

// Where a click would take the reader inside the app: a link's own href, or the path a control
// navigates to itself and marks with data-nav-to. Anything leaving the app — another origin, a
// new tab — is left alone, because the guard cannot follow it anyway.
function navigationTarget(element: Element | null): string | undefined {
  if (!element) return undefined

  const programmatic = element.getAttribute('data-nav-to')
  if (programmatic) return programmatic

  const anchor = element as HTMLAnchorElement
  if (anchor.target && anchor.target !== '_self') return undefined
  if (anchor.origin !== window.location.origin) return undefined

  return anchor.pathname + anchor.search
}
